/**
 * BookGridCard Component - Simplified Grid Card
 * 
 * Clean, scannable grid card keeping only:
 * - Image/cover (with gradient placeholder)
 * - Title (max 2 lines)
 * - Price (or FREE)
 * - Save button
 * 
 * No excessive tags, metadata, or multiple buttons.
 */

import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, FileText } from 'lucide-react';
import { formatMoney } from '../../utils/formatters';
import { trackOpen, trackSave } from '../../utils/tracker';
import { toAbsoluteUrl } from '../../lib/media';
import './BookGridCard.css';

// Placeholder colors
const PLACEHOLDER_COLORS = [
  ['#3B82F6', '#1D4ED8'],
  ['#8B5CF6', '#6D28D9'],
  ['#10B981', '#047857'],
  ['#F59E0B', '#D97706'],
  ['#EF4444', '#DC2626'],
  ['#EC4899', '#BE185D'],
  ['#06B6D4', '#0E7490'],
];

function getPlaceholderGradient(title) {
  const code = (title || 'B').charCodeAt(0) % PLACEHOLDER_COLORS.length;
  return PLACEHOLDER_COLORS[code];
}

function getInitials(title) {
  if (!title) return '?';
  const words = title.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].substring(0, 2).toUpperCase();
}

const BookGridCard = memo(function BookGridCard({
  book,
  isSaved = false,
  onSave,
  isRTL = false,
  lang = 'en',
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [imageError, setImageError] = useState(false);
  
  const actualRTL = isRTL || i18n.language?.startsWith('ar');
  const actualLang = lang || (actualRTL ? 'ar' : 'en');
  
  // Title
  const title = actualRTL && book.title_ar ? book.title_ar : book.title;
  
  // Pricing
  const isFree = book.mode === 'free' || book.price === 0;
  const priceDisplay = isFree 
    ? (actualRTL ? 'مجاني' : 'Free')
    : formatMoney(book.price, book.currency || 'SDG', actualLang);
  
  // Placeholder
  const [gradStart, gradEnd] = getPlaceholderGradient(title);
  const initials = getInitials(title);
  
  const handleCardClick = () => {
    trackOpen(book.id, book.category || 'books_notes');
    navigate(`/student-hub/books/${book.id}`, { state: { book } });
  };
  
  const handleSaveClick = (e) => {
    e.stopPropagation();
    trackSave(book.id, book.category || 'books_notes', !isSaved);
    onSave?.(book.id);
  };

  return (
    <article
      className="book-grid-card"
      onClick={handleCardClick}
      dir={actualRTL ? 'rtl' : 'ltr'}
      data-testid={`book-grid-${book.id}`}
    >
      {/* Thumbnail */}
      <div className="bgc-thumbnail">
        {(book.images?.[0] || book.imageUrl) && !imageError ? (
          <img
            src={toAbsoluteUrl(book.images?.[0] || book.imageUrl)}
            alt={title}
            loading="lazy"
            onError={() => setImageError(true)}
          />
        ) : (
          <div 
            className="bgc-placeholder"
            style={{ background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})` }}
          >
            <span className="bgc-initials">{initials}</span>
            <FileText size={14} className="bgc-file-icon" />
          </div>
        )}
        
        {/* Save button */}
        <button
          className={`bgc-save ${isSaved ? 'saved' : ''}`}
          onClick={handleSaveClick}
          aria-label="Save"
          data-testid={`save-grid-${book.id}`}
        >
          <Heart size={16} fill={isSaved ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Content - Simple: Title + Price only */}
      <div className="bgc-content">
        <h3 className="bgc-title">{title}</h3>
        <span className={`bgc-price ${isFree ? 'free' : ''}`}>
          {priceDisplay}
        </span>
      </div>
    </article>
  );
});

export default BookGridCard;
